import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang, Language} from 'yougile-lang'
import {logger, Time, UUID} from 'yougile-util'
import {Hub, HubProperties, User} from 'yougile-model'
import {Api} from '@api/api'
import {initApi} from '@api/browser-console-api'
import {browserContextObjectStore} from '@api/browser-context-object-store'
import {UIRegistry} from '@api/model/ui-registry'
import {Accounts} from '@client/accounts'
import {DepartmentsClient} from '@client/departments-client'
import {DirectMessages} from '@client/direct-messages'
import {EventBus} from '@client/event-bus'
import {backFromChatEvent, minimizeChatEvent} from '@client/events'
import {LazyLoadClient} from '@client/lazy-load-client'
import {PartnerClient} from '@client/partner-client'
import {PermissionsHelper} from '@client/permissions-helper'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {Backend} from '@common/backend'
import {FileChooser} from '@common/file-chooser'
import {Notifications} from '@common/notifications'
import {UtilUi} from '@common/utils-ui'
import {BoardTabBg} from '@components/board/board-tab-bg'
import {MainMenuStore} from '@components/main-menu/main-menu-store'
import {MessageBox} from '@components/message-box'
import {RepoNavigator} from '@components/repo-navigator'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {taskConnectionStore} from '@components/task-card/task-connection-store'
import {createUseStore, getChatKind} from '@containers/hooks'
import {AppPersistence} from '@extensions/app-persistence'
import {ChatUtils} from '@extensions/chat-utils'
import {Company} from '@extensions/company'
import {Feedback} from '@extensions/feedback'
import {FirstProject} from '@extensions/first-project/first-project'
import {Gantt} from '@extensions/gantt'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {KeyboardHandlers} from '@extensions/keyboard-handlers'
import {MessageNotifier} from '@extensions/message-notifier'
import {Page, pagesWithBackground} from '@extensions/page'
import {PageService} from '@extensions/page-service'
import {Project} from '@extensions/project'
import {Sticker} from '@extensions/sticker/sticker'
import {SystemEntities} from '@extensions/system-entities'
import {ThemeUtils} from '@extensions/theme-utils'

export const BlockColumnSortWarningKey = 'block-column-sort-warning'
export const BlockGanttAutoPlanningWarningKey =
  'block-gantt-auto-planning-warning'

interface State {
  chatHub?: Hub
  page?: Page
  subpage?: string
  subsubpage?: string

  taskToConnect: Hub | undefined
  taskToConnectType: 'connection' | 'dependency' | 'dependent' | undefined
  scriptsOpen: boolean

  syncId: number
  chatChangeListener?: UUID

  ready?: boolean

  blockColumnSortWarning?: boolean
  blockGanttAutoPlanningWarning?: boolean
}

export class LoggedInStore {
  state: State

  constructor(public mainMenuStore: MainMenuStore) {
    const syncId = SyncEvents.addListener(
      Sync.profileId,
      globals.currentUserId,
      ThemeUtils.upTheme
    )
    this.state = {
      taskToConnect: undefined,
      taskToConnectType: undefined,
      chatHub: ChatUtils.getOpenedChatHub(),
      syncId,
      scriptsOpen: false
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        mainMenuStore: false
      },
      {autoBind: true}
    )

    this.syncBlockColumnSortWarning()
    this.syncBlockGanttAutoPlanningWarning()
  }

  initialize(): void {
    void ThemeUtils.upTheme()

    this.state.chatChangeListener = ChatUtils.addChatChangeListener(() => {
      const newChat = ChatUtils.getOpenedChatHub()
      if (
        (this.state.chatHub &&
          newChat &&
          this.state.chatHub.id !== newChat.id) ||
        +!!this.state.chatHub - +!!newChat !== 0
      ) {
        this.state.chatHub = newChat
      }
    })
    void this.initializeOnEnter()
  }

  dispose(): void {
    SyncEvents.removeListener(this.state.syncId)
    const chatCntEl = document.querySelector<HTMLElement>('.chat-item-cnt')
    if (chatCntEl) {
      UtilUi.removeEl(chatCntEl)
    }
    ChatUtils.removeChatChangeListener(this.state.chatChangeListener)
  }

  setPage(): void {
    this.state.page = PageService.getPage()
    this.state.subpage = PageService.getSubpage()
    this.state.subsubpage = PageService.getSubpage(3)
  }

  syncBlockColumnSortWarning(): void {
    this.state.blockColumnSortWarning =
      Storage.getItem(BlockColumnSortWarningKey) === 'true'
  }

  setBlockColumnSortWarning(value: 'true' | 'false'): void {
    Storage.setItem(BlockColumnSortWarningKey, value)
    this.syncBlockColumnSortWarning()
  }

  syncBlockGanttAutoPlanningWarning(): void {
    this.state.blockGanttAutoPlanningWarning =
      Storage.getItem(BlockGanttAutoPlanningWarningKey) === 'true'
  }

  setBlockGanttAutoPlanningWarning(value: 'true' | 'false'): void {
    Storage.setItem(BlockGanttAutoPlanningWarningKey, value)
    this.syncBlockGanttAutoPlanningWarning()
  }

  async initializeOnEnter(): Promise<void> {
    globals.scriptsOpenUpdated = () => {
      this.state.scriptsOpen = globals.scriptsOpen
    }

    PageService.addLocationListener(this.setPage)
    await Company.init()
    LazyLoadClient.init()
    AppPersistence.init()
    void PageService.up()
    void RepoNavigator.init()
    MessageNotifier.init(Notifications.notify, Notifications.ring)
    Feedback.init()
    ChatUtils.init()
    DepartmentsClient.init()
    PermissionsHelper.init()
    Project.init()
    UIRegistry.init()
    PartnerClient.init()
    KeyboardHandlers.init()
    Gantt.init()
    GanttDraft.init()

    // initializing system entities
    Sync.listSandboxes().forEach(box => {
      SystemEntities.get(box.companyId)
      Sticker.init(box)
    })
    SyncEvents.addNewCompanyListener(companyId => {
      const box = Sync.get({companyId})
      SystemEntities.get(box.companyId)
      Sticker.init(box)
    })

    // fixing old user structure
    const profile = Sync.getProfile()
    const profileHub = profile.get(profile.companyId) as Hub
    const version = profileHub.properties[HubProperties.dataVersion]
    if (!version || +version < 2) {
      logger.log('Migrating to new profile user structure')
      profileHub.properties[HubProperties.dataVersion] = 2
      profileHub.commit()
      profile.getAllUsers(true).forEach((u: User) => {
        profile.restoreUserInProfile(u.id)
      })
    }

    // restoring users who wrote new messages
    profile.getAllUsers(true).forEach((u: User) => {
      if (!profile.isUserInCompany(u.id)) {
        const chat = DirectMessages.getChatIfExists(u.id)
        if (chat?.chat.newMessagesFor(globals.currentUserId)) {
          profile.restoreUserInProfile(u.id)
        }
      }
    })

    if (globals.currentUserId) {
      Accounts.createAccountInEveryCompany()
      const user = Sync.getProfile().get(globals.currentUserId) as User
      const langLoc = Backend.loadLocal('lang') as Language

      if (langLoc && user.language !== langLoc && !SharedBoardsClient.getId()) {
        logger.log('Change user language to ' + langLoc)
        user.language = langLoc
        user.commit()
      }
      BoardTabBg.init()
      FirstProject.init(() => PageService.setUrl('/'))
      if (!SharedBoardsClient.getId()) {
        Lang.setLang(langLoc || user.language)
      }
      Backend.saveLocal('lang', Lang.getLang())
      Lang.addLangChangeHandler(() => Backend.saveLocal('lang', Lang.getLang()))

      const timeDiff = Time.getTimeDiff()
      if (timeDiff && Math.abs(user.timeDiff - timeDiff) > 60000) {
        user.timeDiff = timeDiff
        user.commit()
      }

      Api.init(
        (onFileChoose: (file: File) => void) => {
          void FileChooser.chooseFile(
            document.createEvent('MouseEvent'),
            true,
            onFileChoose
          )
        },
        MessageBox.show,
        browserContextObjectStore
      )

      initApi()
    }
    runInAction(() => (this.state.ready = true))
  }

  onEscape(): boolean | void {
    if (globals.taskToConnect) {
      this.connectionStoreClear()
      return
    }

    if (rightPanelStore.state.chatPanel.opened) {
      if (getChatKind() !== 'empty') {
        EventBus.dispatch(backFromChatEvent())
      } else {
        EventBus.dispatch(minimizeChatEvent())
      }
      return true
    } else if (this.isMenuOpen) {
      this.mainMenuStore.setMenuOpen(false)
      return true
    }
  }

  isEmptySharing(): boolean {
    return (
      this.state.page === 'loading' &&
      SharedBoardsClient.isGuest() &&
      !PageService.getCurrentProject()
    )
  }

  get isPageWithBg(): boolean {
    return !!this.state.page && pagesWithBackground.includes(this.state.page)
  }

  getBg(): string {
    return BoardTabBg.getBackgroundImage()
  }

  connectionStoreClear(): void {
    globals.taskToConnect = undefined
    taskConnectionStore.clear()
  }

  hasMenu(): boolean {
    const noFrame = window.parent === window
    return noFrame && !SharedBoardsClient.getId()
  }

  get rightMargin(): string {
    const {opened, width, resize} = rightPanelStore.state.chatPanel
    return (opened && !resize ? width : 0) + 'px'
  }

  get leftMargin(): string {
    const mainMenuMargin = this.isMenuOpen ? 240 : 48
    return `${mainMenuMargin}px`
  }

  get isMenuOpen(): boolean {
    return this.hasMenu() && !!this.mainMenuStore.state.menuOpen
  }
}

export const [LoggedInContext, useLoggedInStore] = createUseStore(LoggedInStore)
